/**
 * Enemy Attack Telegraph / Red Zone System ("Niat Serangan Musuh")
 * 
 * Accurately displays transparent red danger zones on the ground during enemy wind-up.
 * CRITICAL RULE: The red zone warning immediately disappears when the enemy begins its actual attack.
 * Warning zone != damage hitbox.
 */

import { HitboxShape } from "./HitboxSystem";

export interface ActiveTelegraph {
  id: string;
  sourceEntityId: string;
  shape: HitboxShape;
  durationMs: number;
  elapsedMs: number;
  opacity: number;
}

export class TelegraphSystem {
  private telegraphs: Map<string, ActiveTelegraph> = new Map();

  /**
   * Register a new attack telegraph for an enemy entity.
   */
  public createTelegraph(sourceEntityId: string, shape: HitboxShape, durationMs: number): string {
    const id = `tele_${sourceEntityId}_${Date.now()}`;
    this.telegraphs.set(sourceEntityId, {
      id,
      sourceEntityId,
      shape,
      durationMs,
      elapsedMs: 0,
      opacity: 0.28,
    });
    return id;
  }

  /**
   * Immediately remove the red zone when enemy windup finishes and attack initiates.
   */
  public clearTelegraph(sourceEntityId: string) {
    this.telegraphs.delete(sourceEntityId);
  }

  /**
   * Update active telegraph timers.
   */
  public update(dtMs: number) {
    for (const [key, tele] of this.telegraphs.entries()) {
      tele.elapsedMs += dtMs;
      // If elapsed reaches duration, it will be cleared by the attacking enemy state transition
      if (tele.elapsedMs >= tele.durationMs) {
        this.telegraphs.delete(key);
      }
    }
  }

  /**
   * Render all ground red zones before actual damage occurs.
   */
  public render(ctx: CanvasRenderingContext2D) {
    if (this.telegraphs.size === 0) return;

    ctx.save();

    for (const tele of this.telegraphs.values()) {
      const progress = Math.min(1, tele.elapsedMs / tele.durationMs);
      const s = tele.shape;
      const isCritical = progress >= 0.75;
      const pulseAlpha = isCritical ? 0.38 + Math.sin(tele.elapsedMs * 0.03) * 0.12 : tele.opacity;

      ctx.save();
      // Razor-sharp danger red zone
      ctx.fillStyle = `rgba(255, 28, 48, ${pulseAlpha})`;
      ctx.strokeStyle = isCritical ? "rgba(255, 255, 255, 0.95)" : "rgba(255, 40, 60, 0.95)";
      ctx.lineWidth = isCritical ? 2.5 : 2;
      ctx.shadowColor = "#ff2233";
      ctx.shadowBlur = isCritical ? 8 : 4;

      if (s.type === "circle") {
        // Base ground danger zone
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // Epicenter crosshair
        ctx.lineWidth = 1;
        ctx.strokeStyle = "rgba(255, 100, 100, 0.6)";
        ctx.beginPath();
        ctx.moveTo(s.x - 8, s.y);
        ctx.lineTo(s.x + 8, s.y);
        ctx.moveTo(s.x, s.y - 8);
        ctx.lineTo(s.x, s.y + 8);
        ctx.stroke();

        // Expanding inner charge ring
        ctx.fillStyle = "rgba(255, 20, 30, 0.28)";
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.radius * progress, 0, Math.PI * 2);
        ctx.fill();
      } else if (s.type === "rect") {
        ctx.save();
        ctx.translate(s.x, s.y);
        ctx.rotate(s.angle);

        // Danger zone rectangle
        ctx.beginPath();
        ctx.rect(-s.width / 2, -s.height / 2, s.width, s.height);
        ctx.fill();
        ctx.stroke();

        // Expanding danger fill along attack strike direction (height/length)
        ctx.fillStyle = "rgba(255, 30, 40, 0.32)";
        ctx.beginPath();
        ctx.rect(-s.width / 2, -s.height / 2, s.width, s.height * progress);
        ctx.fill();

        ctx.restore();
      } else if (s.type === "cone") {
        // Danger sector
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.radius, s.angle - s.spreadAngle / 2, s.angle + s.spreadAngle / 2);
        ctx.lineTo(s.x, s.y);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // Expanding inner charge sector
        ctx.fillStyle = "rgba(255, 25, 35, 0.3)";
        ctx.beginPath();
        ctx.arc(
          s.x,
          s.y,
          s.radius * progress,
          s.angle - s.spreadAngle / 2,
          s.angle + s.spreadAngle / 2
        );
        ctx.lineTo(s.x, s.y);
        ctx.closePath();
        ctx.fill();
      } else if (s.type === "line") {
        ctx.lineWidth = s.thickness;
        ctx.strokeStyle = `rgba(255, 30, 45, ${pulseAlpha})`;
        ctx.beginPath();
        ctx.moveTo(s.x1, s.y1);
        ctx.lineTo(s.x2, s.y2);
        ctx.stroke();

        // Outer crisp outline
        ctx.lineWidth = 2;
        ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
        ctx.stroke();
      }

      ctx.restore();
    }

    ctx.restore();
  }

  public clearAll() {
    this.telegraphs.clear();
  }
}
